import dayjs from 'dayjs'
import type { RulePrimitive, RuleValue } from '@/types/types'
import type {
  ApiAudienceRule,
  ComponentAudienceRule,
  DateFilter,
  RuleParameters,
  AudienceRuleRecord
} from '@/types/audience'

const ARRAY_OPERATORS = ['between', 'in', 'ni'] as const

export function isArrayOperator(operator: string): boolean {
  return ARRAY_OPERATORS.includes(operator as (typeof ARRAY_OPERATORS)[number])
}

export function isDatePickerFormat(dateString: string): boolean {
  return /\d{2}\/\d{2}\/\d{4}/.test(dateString)
}

export function normalizeRuleValue(value: RuleValue): RuleValue {
  if (Array.isArray(value)) {
    return value
  }
  if (typeof value === 'number') {
    return [value]
  }
  return [value]
}

function isRulePrimitiveFilled(value: RulePrimitive | undefined): boolean {
  return value !== null && value !== undefined && value !== ''
}

export function isBetweenRuleIncomplete(rule?: { operator?: string; value?: RuleValue }): boolean {
  if (rule?.operator !== 'between') {
    return false
  }

  const value = rule.value
  const values = Array.isArray(value) ? value : [value]

  return !isRulePrimitiveFilled(values[0]) || !isRulePrimitiveFilled(values[1])
}

export function normalizeDateFilterValue(value: RuleValue): RuleValue {
  return normalizeRuleValue(value)
}

export function attributeToKebabCase(attribute: string): string {
  return attribute.replaceAll(/_/g, '-')
}

export function attributeToSnakeCase(attribute: string): string {
  return attribute.replaceAll(/-/g, '_')
}

function convertDateToApiFormat(dateString: string): string | undefined {
  if (dayjs(dateString, 'DD/MM/YYYY').isValid()) {
    return dayjs(dateString, 'DD/MM/YYYY').format('YYYY-MM-DD')
  }
  return undefined
}

function convertValueDatesToApiFormat(value: RuleValue): RuleValue {
  if (typeof value === 'string') {
    if (isDatePickerFormat(value)) {
      return convertDateToApiFormat(value) ?? value
    }
    return value
  }

  if (Array.isArray(value) && value.length > 0) {
    if (typeof value[0] === 'string' && isDatePickerFormat(value[0])) {
      return value.map((v) => (typeof v === 'string' && isDatePickerFormat(v) ? (convertDateToApiFormat(v) ?? v) : v))
    }
  }

  return value
}

function formatDateFilterForApi(dateFilter: DateFilter): DateFilter | null {
  const { value, type, operator } = dateFilter

  if (!type || !operator) {
    return null
  }

  if (Array.isArray(value) && value.length === 0) {
    return null
  }

  if (type === 'relative') {
    const numericValue = Array.isArray(value) ? Number(value[0]) : Number(value)
    if (isNaN(numericValue)) {
      return null
    }
    return {
      type,
      operator,
      value: numericValue
    }
  }

  const normalizedValue = normalizeDateFilterValue(value)
  const convertedValue = convertValueDatesToApiFormat(normalizedValue)
  const finalValue: RuleValue = isArrayOperator(operator)
    ? convertedValue
    : Array.isArray(convertedValue)
      ? convertedValue[0]
      : convertedValue

  return {
    type,
    operator,
    value: finalValue
  }
}

function isDateFilterValid(dateFilter: DateFilter): boolean {
  if (!dateFilter.type || !dateFilter.operator) {
    return false
  }
  if (Array.isArray(dateFilter.value) && dateFilter.value.length === 0) {
    return false
  }
  return true
}

function formatRuleParametersForApi(parameters: RuleParameters): RuleParameters | null {
  const formattedParameters: RuleParameters = {}

  if (parameters.date_filter && isDateFilterValid(parameters.date_filter)) {
    const formattedDateFilter = formatDateFilterForApi(parameters.date_filter)
    if (formattedDateFilter) {
      formattedParameters.date_filter = formattedDateFilter
    }
  }

  if (parameters.quantity_filter) {
    const { value, operator } = parameters.quantity_filter
    const numericValue = Array.isArray(value) ? Number(value[0]) : Number(value)
    if (!isNaN(numericValue)) {
      formattedParameters.quantity_filter = {
        operator,
        value: numericValue
      }
    }
  }

  return Object.keys(formattedParameters).length > 0 ? formattedParameters : null
}

function extractRuleValue(rule: ComponentAudienceRule): RuleValue {
  const normalizedValue = normalizeRuleValue(rule.value)
  return isArrayOperator(rule.operator)
    ? normalizedValue
    : Array.isArray(normalizedValue)
      ? normalizedValue[0]
      : normalizedValue
}

function formatRuleValueForApi(value: RuleValue): RuleValue {
  const convertedValue = convertValueDatesToApiFormat(value)
  return convertedValue
}

export function transformComponentRuleToApi(rule: ComponentAudienceRule): ApiAudienceRule | null {
  const mainRuleValue = extractRuleValue(rule)
  const formattedValue = formatRuleValueForApi(mainRuleValue)

  const apiRule: ApiAudienceRule = {
    attribute: attributeToSnakeCase(rule.attribute),
    operator: rule.operator,
    value: formattedValue
  }

  if (rule.parameters) {
    const formattedParameters = formatRuleParametersForApi(rule.parameters)
    if (formattedParameters) {
      apiRule.parameters = formattedParameters
    }
  }

  return apiRule
}

export function transformApiRuleToComponent(apiRule: ApiAudienceRule): ComponentAudienceRule {
  const normalizedValue = normalizeRuleValue(apiRule.value)

  const componentRule: ComponentAudienceRule = {
    attribute: attributeToKebabCase(apiRule.attribute),
    operator: apiRule.operator,
    value: normalizedValue
  }

  if (apiRule.parameters) {
    const normalizedParameters: RuleParameters = {}

    if (
      apiRule.parameters.date_filter &&
      apiRule.parameters.date_filter.type &&
      apiRule.parameters.date_filter.operator
    ) {
      normalizedParameters.date_filter = {
        ...apiRule.parameters.date_filter,
        value: normalizeDateFilterValue(apiRule.parameters.date_filter.value)
      }
    }

    if (apiRule.parameters.quantity_filter) {
      normalizedParameters.quantity_filter = apiRule.parameters.quantity_filter
    }

    if (Object.keys(normalizedParameters).length > 0) {
      componentRule.parameters = normalizedParameters
    }
  }

  return componentRule
}

export function transformRuleRecordToApiArray(rules: AudienceRuleRecord): ApiAudienceRule[] {
  return Object.entries(rules)
    .map(([, rule]) => transformComponentRuleToApi(rule))
    .filter((rule): rule is ApiAudienceRule => rule !== null)
}

export function transformApiArrayToRuleRecord(apiRules: ApiAudienceRule[]): AudienceRuleRecord {
  const record: AudienceRuleRecord = {}
  apiRules.forEach((apiRule) => {
    const componentRule = transformApiRuleToComponent(apiRule)
    record[componentRule.attribute] = componentRule
  })
  return record
}
